'use client';

// Page Tarifs — premier bout-en-bout front↔API (GET /api/subscriptions/plans, public).
// Prouve la chaîne complète : client API cookie-aware → enveloppe → rendu tokenisé + i18n.

import { useT } from '@/hooks/useT';
import { usePlans } from '@/queries/plans';
import { formatPrice, formatStorage } from '@/lib/format';
import { fontStyle } from '@/theme/typography';
import type { Plan } from '@/types/api';

export default function TarifsPage() {
  const { t, i18n } = useT();
  const { data: plans, isPending, isError, refetch } = usePlans();
  const locale = i18n.language;

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-6 py-16">
      <header className="flex flex-col gap-2 text-center">
        <h1 style={fontStyle('displayXl')} className="font-display text-text-primary">
          {t('pricing.title')}
        </h1>
        <p style={fontStyle('bodyLg')} className="font-body text-text-secondary">
          {t('pricing.subtitle')}
        </p>
      </header>

      {isPending && (
        <p style={fontStyle('body')} className="text-center text-text-secondary">
          {t('state.loading')}
        </p>
      )}

      {isError && (
        <div className="flex flex-col items-center gap-3">
          <p style={fontStyle('body')} className="text-danger">
            {t('state.error')}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            style={fontStyle('button')}
            className="rounded-md bg-action px-4 py-2 text-action-on"
          >
            {t('actions.retry')}
          </button>
        </div>
      )}

      {plans && (
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <PlanCard key={plan.code} plan={plan} locale={locale} />
          ))}
        </section>
      )}
    </main>
  );
}

function PlanCard({ plan, locale }: { plan: Plan; locale: string }) {
  const { t } = useT();
  const { pricing, limits } = plan;
  const isFree = pricing.monthly_cents === 0;
  const unlimited = t('pricing.unlimited');
  const orUnlimited = (n: number | null) => (n === null ? unlimited : n);

  return (
    <article className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-6 shadow-[var(--rmbr-shadow-card)]">
      <h2 style={fontStyle('subtitle')} className="font-display text-text-primary">
        {t(`pricing.plan.${plan.code}`)}
      </h2>

      <div className="flex items-baseline gap-1">
        {isFree ? (
          <span style={fontStyle('title')} className="font-display text-brand">
            {t('pricing.free')}
          </span>
        ) : (
          <>
            <span style={fontStyle('title')} className="font-display text-text-primary">
              {formatPrice(pricing.monthly_cents, pricing.currency, locale)}
            </span>
            <span style={fontStyle('bodySm')} className="text-text-tertiary">
              {t('pricing.perMonth')}
            </span>
          </>
        )}
      </div>

      <ul className="flex flex-col gap-2 border-t border-border pt-4">
        {[
          t('pricing.limits.memories', { count: orUnlimited(limits.max_memories) }),
          t('pricing.limits.moments', { count: orUnlimited(limits.max_moments_per_memory) }),
          t('pricing.limits.invitees', { count: orUnlimited(limits.max_invitees_per_memory) }),
          t('pricing.limits.storage', {
            value: limits.max_storage_bytes === null ? unlimited : formatStorage(limits.max_storage_bytes, locale),
          }),
          t('pricing.limits.roles', { roles: plan.allowed_roles.join(', ') }),
        ].map((line, i) => (
          <li key={i} style={fontStyle('bodySm')} className="text-text-secondary">
            {line}
          </li>
        ))}
      </ul>
    </article>
  );
}
